import * as React from "react";
import HeaderBar from "./HeaderBar";
import Collapside from "./Collapside";
import { AvailableEntitiesSettings } from "../model/EntitySettings";
import LoadingSpinner from "../../flow/utils/LoadingSpinner";

export interface Catalogable {
  uid: string;
  name: string;
}

interface CatalogLabels {
  title: string;
  searchPlaceholder: string;
  createButton: string;
}

interface itemsByType<T> {
  type?: string;
  items: T[];
  createUrl?: string;
  itemsByCategory?: {
    items: T[];
    title: string;
    color: string;
  }[];
}

interface CatalogViewerProps<T> {
  itemsByType: itemsByType<T>[];
  itemRenderer: (item: T) => React.ReactElement;
  labels: CatalogLabels;
  itemSpacerClass?: (currentItem: T, nextItem?: T) => string;
  extraContent?: (currentItem: T) => React.ReactElement;
  extraClasses?: string;
  isEmailCatalogTemplate?: boolean;
  entities: AvailableEntitiesSettings;
}

interface CatalogViewerState<T> {
  selectedItem: T;
  filter: string;
  loading: boolean;
}

export default class CatalogViewer<T extends Catalogable> extends React.Component<
  CatalogViewerProps<T>,
  CatalogViewerState<T>
> {
  private readonly items: T[];

  constructor(props) {
    super(props);
    this.state = {
      filter: "",
      selectedItem: this.props.itemsByType[0].items.sort(this.byName)[0],
      loading: false,
    };
    this.items = this.props.itemsByType
      .map((it) => it.items)
      .reduce((acc, val) => acc.concat(val), []); // Flatten
  }

  render() {
    if (this.state.loading) {
      return <LoadingSpinner />;
    }

    const filteredItemsByType: itemsByType<T>[] = this.props.itemsByType.map((it) => {
      return {
        ...it,
        items: it.items.filter(this.filterByName(this.state.filter)),
        itemsByCategory: it.itemsByCategory
          ? [
              ...it?.itemsByCategory?.map((itemByCategory) => {
                return {
                  ...itemByCategory,
                  items: itemByCategory.items.filter(this.filterByName(this.state.filter)),
                };
              }),
            ]
          : [],
      };
    });
    const hasCreateButton = this.props.itemsByType.some((it) => it.createUrl);
    const hasItems = filteredItemsByType.some((it) => it.items.length > 0);
    return (
      <div
        className={
          "catalog v-flex no-wrap " + (this.props.extraClasses ? this.props.extraClasses : "")
        }
      >
        <HeaderBar
          title={this.props.labels.title}
          searchConfiguration={{
            value: this.state.filter,
            onChange: (filter) =>
              this.setState({
                filter,
                selectedItem: this.items.filter(this.filterByName(filter))[0],
              }),
            placeholder: this.props.labels.searchPlaceholder,
          }}
          entities={this.props.entities}
          onChange={() => {
            this.setState({ loading: true });
          }}
        />
        <div className="content h-flex">
          {hasCreateButton || hasItems ? (
            <>
              <div className="list">
                {filteredItemsByType
                  .filter((it) => it.createUrl || it.items.length > 0)
                  .map((it) =>
                    this.props.isEmailCatalogTemplate
                      ? this.renderEmailItemsByType(it)
                      : this.renderItemsByType(it)
                  )}
              </div>
              {hasItems ? (
                <div className="viewer">
                  {this.state.selectedItem && this.props.itemRenderer(this.state.selectedItem)}
                </div>
              ) : (
                <div className="no-result">{i18n["Page.Catalog.Search.NoResult"]}</div>
              )}
            </>
          ) : (
            <div className="no-result">{i18n["Page.Catalog.Search.NoResult"]}</div>
          )}
        </div>
      </div>
    );
  }

  private renderItemsByType(itemsByType: itemsByType<T>) {
    return (
      <>
        {itemsByType.type && (
          <div className="item-category" key={itemsByType.type}>
            {itemsByType.type}
          </div>
        )}
        {itemsByType.createUrl && (
          <a className="create-button" href={itemsByType.createUrl}>
            <i className="ic_plus" />
            <span>{this.props.labels.createButton}</span>
          </a>
        )}
        {!!itemsByType.items && (
          <div className="item-container">
            {itemsByType.items.sort(this.byName).map((item, i, sortedItems) => (
              <div
                key={item.uid}
                id={`catalog-item-${item.uid}`}
                onClick={() => this.setState({ selectedItem: item })}
                className={
                  "item clickable h-flex space-between" +
                  this.selectedItemClass(item) +
                  (this.props.itemSpacerClass
                    ? this.props.itemSpacerClass(item, sortedItems[i + 1])
                    : "")
                }
              >
                {item.name}
                {this.props.extraContent && this.props.extraContent(item)}
              </div>
            ))}
          </div>
        )}
      </>
    );
  }

  private itemsRender = (itemsByType: itemsByType<T>) => (
    <>
      {itemsByType.items.sort(this.byName).map((item, i, sortedItems) => (
        <div
          key={item.uid}
          id={`catalog-item-${item.uid}`}
          onClick={() => this.setState({ selectedItem: item })}
          className={
            "clickable email-template-item " +
            this.selectedItemClass(item) +
            (this.props.itemSpacerClass ? this.props.itemSpacerClass(item, sortedItems[i + 1]) : "")
          }
        >
          <i className="file-icon"></i>
          <div>
            {item.name}
            {this.props.extraContent && this.props.extraContent(item)}
          </div>
        </div>
      ))}
    </>
  );

  private renderEmailItemsByType(itemsByType: itemsByType<T>) {
    return (
      <>
        {itemsByType.type && <h4>{itemsByType.type}</h4>}
        {itemsByType.createUrl && (
          <a className="create-button" href={itemsByType.createUrl}>
            <i className="ic_plus" />
            <span>{this.props.labels.createButton}</span>
          </a>
        )}

        {itemsByType.itemsByCategory.map(
          (itemsByCategory) =>
            itemsByCategory.items.length !== 0 && (
              <Collapside<T>
                key={itemsByCategory.title}
                itemsRender={this.itemsRender}
                category={itemsByCategory}
                itemsFilter={this.state.filter}
              ></Collapside>
            )
        )}
      </>
    );
  }

  componentDidMount() {
    const itemName = decodeURI(document.location.hash.substring(1));
    if (itemName) {
      const selectedItem = this.items.find((t) => t.name === itemName);
      this.setState({ selectedItem });
      if (selectedItem) {
        document.querySelector(`#catalog-item-${selectedItem.uid}`).scrollIntoView({
          block: "center",
          behavior: "smooth",
        });
      }
    }
  }

  private filterByName(filter: string) {
    return (item: T) => !filter || item.name.toLowerCase().indexOf(filter.toLowerCase()) > -1;
  }

  private byName(i1: T, i2: T) {
    const name1 = i1.name.toLowerCase(),
      name2 = i2.name.toLowerCase();
    if (name1 < name2) return -1;
    else if (name1 > name2) return 1;
    else return 0;
  }

  private selectedItemClass(currentItem: T): string {
    return currentItem === this.state.selectedItem ? " selected" : "";
  }
}
